import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from 'convex/react';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import useAppleMusicEnrichment from '../../../hooks/useAppleMusicEnrichment';
import useTrackPlays from '../../../hooks/useTrackPlays';
import { usePlaylist } from '../../../contexts/PlaylistContext';
import NowPlayingBars from '../../cards/NowPlayingBars';
import { formatDate, getArtistPath, getOptimizedImageUrl, safeStringWithHyphens, trackClick } from '../../_helpers';
import { API_CACHE_TTL, fetchCachedGetApi } from '../../../utils/apiCache';
import { getAppleMusicEnrichmentKey } from '../../../utils/appleMusicEnrichment';
import { COMPANION_ARTIST_SLUG } from '../../../utils/companionPlaylists';
import { shouldIncludeSpotifyTrackForArtist } from '../../../utils/spotifyArtistCredits';
import { sharedProps } from '../../../utils/sharedElements';

interface SpotifyAlbumSummary {
  id: string;
  name: string;
  images?: Array<{ url: string }>;
  release_date: string;
  album_type?: string;
  album_group?: string;
  total_tracks?: number;
  artists?: Array<{ name: string }>;
}

interface SpotifyTrackItem {
  id: string;
  name: string;
  artists?: Array<{ name: string }>;
}

interface LatestTrackEntry {
  album: SpotifyAlbumSummary;
  track: SpotifyTrackItem;
}

interface LatestReleaseGroup {
  id: string;
  title: string;
  image?: string;
  releaseDate: string;
  totalTracks: number;
  type: 'album' | 'ep' | 'single';
  path: string;
  appleMusicUrl?: string;
  appleMusicArtworkUrl?: string;
}

interface PopularTracksProps {
  artistName: string;
  topTracks?: Array<{
    id: string;
    name: string;
    album?: {
      images?: Array<{ url: string }>;
      name?: string;
    };
  }>;
  albums?: SpotifyAlbumSummary[];
  mobileLatestOnly?: boolean;
  hideLatestHeader?: boolean;
  featuredReleaseId?: string;
  featuredReleaseName?: string;
}

const INITIAL_LATEST_RELEASES = 5;
const MOBILE_VISIBLE_RELEASES = 5;

const FireIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 23C16.1421 23 19.5 19.6421 19.5 15.5C19.5 13.8 18.9 12.2 17.9 10.9C17.4 11.7 16.6 12.3 15.7 12.5C16.3 11.4 16.5 10.1 16.3 8.9C16.1 7.7 15.5 6.6 14.6 5.7C14.6 6.7 14.3 7.7 13.7 8.5C13.1 9.3 12.2 9.9 11.2 10.1C11.6 8.4 11.4 6.6 10.6 5C9.8 3.5 8.5 2.3 7 1.5C7.3 3.5 6.9 5.5 5.8 7.2C4.7 8.9 3.1 10.2 1.2 11C2.5 13.5 4.9 15.2 7.7 15.5C6.5 16.4 5.9 17.9 6.2 19.4C6.5 20.9 7.7 22.1 9.2 22.4C10.2 22.6 11.1 22.9 12 23Z"
      fill="var(--logoColor2)"
    />
  </svg>
);

const PlayCountBadge = ({ count }: { count: number }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderRadius: '1rem',
      padding: { xs: '0.4rem 0.8rem', md: '0.25rem 0.5rem' },
    }}
  >
    <FireIcon />
    <Typography
      sx={{
        fontSize: { xs: '1rem', md: '0.75rem' },
        fontWeight: 600,
        color: 'var(--logoColor)',
      }}
    >
      {count}
    </Typography>
  </Box>
);

export const TWO_COLUMN_LIST_MIN = 4;

export function twoColumnListSx(count: number) {
  if (count < TWO_COLUMN_LIST_MIN) return { display: 'flex', flexDirection: 'column' } as const;
  return {
    display: { xs: 'flex', md: 'grid' },
    flexDirection: 'column',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridTemplateRows: `repeat(${Math.ceil(count / 2)}, auto)`,
    gridAutoFlow: 'column',
  } as const;
}

const LatestReleaseSkeleton = ({ count = MOBILE_VISIBLE_RELEASES }: { count?: number }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: { xs: '1rem', md: '0.75rem' },
    }}
  >
    {Array.from({ length: count }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: '1.25rem', md: '0.8rem' },
          width: '100%',
          minHeight: { xs: '9.4rem', md: '4rem' },
          p: { xs: '1rem 1.15rem', md: '0.55rem 0.75rem' },
          borderRadius: '8px',
          backgroundColor: { xs: 'var(--logoColor)', md: 'rgba(1, 1, 1, 0.16)' },
          opacity: 0.56,
          overflow: 'hidden',
          '@keyframes latestReleaseSkeletonPulse': {
            '0%': { opacity: 0.38 },
            '50%': { opacity: 0.72 },
            '100%': { opacity: 0.38 },
          },
        }}
      >
        <Box
          sx={{
            width: { xs: '8rem', md: '3rem' },
            height: { xs: '8rem', md: '3rem' },
            borderRadius: '6px',
            flexShrink: 0,
            backgroundColor: 'var(--color5)',
            animation: 'latestReleaseSkeletonPulse 1.35s ease-in-out infinite',
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: '72%',
              height: { xs: '1.35rem', md: '0.8rem' },
              borderRadius: '4px',
              backgroundColor: 'var(--color5)',
              mb: '0.8rem',
              animation: 'latestReleaseSkeletonPulse 1.35s ease-in-out infinite',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
            <Box
              sx={{
                width: '3.5rem',
                height: { xs: '1.5rem', md: '1rem' },
                borderRadius: '999px',
                backgroundColor: 'var(--color5)',
                animation: 'latestReleaseSkeletonPulse 1.35s ease-in-out infinite',
              }}
            />
            <Box
              sx={{
                width: '7rem',
                height: { xs: '1rem', md: '0.65rem' },
                borderRadius: '4px',
                backgroundColor: 'var(--color5)',
                animation: 'latestReleaseSkeletonPulse 1.35s ease-in-out infinite',
              }}
            />
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
);

const getReleasePath = (artistName: string, release: SpotifyAlbumSummary) => {
  const totalTracks = release.total_tracks || 1;
  const section = getReleaseType(totalTracks) === 'single' ? '/songs/' : '/albums/';
  return getArtistPath(artistName, section + safeStringWithHyphens(release.name));
};

const getReleaseType = (totalTracks: number): LatestReleaseGroup['type'] => {
  if (totalTracks >= 7) return 'album';
  if (totalTracks >= 3) return 'ep';
  return 'single';
};

const getReleaseTypeLabel = (releaseType: LatestReleaseGroup['type']) => {
  if (releaseType === 'album') return 'Album';
  if (releaseType === 'ep') return 'EP';
  return 'Single';
};

const normalizeReleaseKey = (value?: string) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const fetchAlbumDetail = async (artistName: string, album: SpotifyAlbumSummary) => {
  const data = await fetchCachedGetApi<{ album?: any }>('/.netlify/functions/SpotifyGetArtistOneAlbum', { artist: artistName, album: album.name }, {
    cacheKey: `latest-release-album:${normalizeReleaseKey(artistName)}:${album.id || normalizeReleaseKey(album.name)}`,
    ttlMs: API_CACHE_TTL.SIX_HOURS,
    shouldCache: (data) => Boolean(data?.album),
  });

  return data.album || null;
};

const buildLatestGroups = (entries: LatestTrackEntry[], artistName: string): LatestReleaseGroup[] => {
  const groups = new Map<string, LatestReleaseGroup>();

  entries.forEach(({ album, track }) => {
    const totalTracks = album.total_tracks || 1;
    const releaseType = getReleaseType(totalTracks);
    const groupId = album.id || track.id;

    if (!groups.has(groupId)) {
      groups.set(groupId, {
        id: groupId,
        title: album.name || track.name,
        image: album.images?.[0]?.url,
        releaseDate: album.release_date,
        totalTracks,
        type: releaseType,
        path: getReleasePath(artistName, album),
      });
    }
  });

  return Array.from(groups.values());
};

const filterFeaturedRelease = (groups: LatestReleaseGroup[], featuredReleaseId?: string, featuredReleaseName?: string) => {
  const featuredId = featuredReleaseId || '';
  const featuredName = normalizeReleaseKey(featuredReleaseName);

  return groups.filter((release) => {
    return release.id !== featuredId && normalizeReleaseKey(release.title) !== featuredName;
  });
};

export function PopularTracks({
  artistName,
  topTracks,
  albums,
  mobileLatestOnly = false,
  hideLatestHeader = false,
  featuredReleaseId,
  featuredReleaseName,
}: PopularTracksProps) {
  const { popularTracks, totalPlays } = useTrackPlays(mobileLatestOnly ? undefined : artistName);
  const { currentTrack, isPlaying } = usePlaylist();
  const playingReleaseKey = isPlaying ? normalizeReleaseKey(currentTrack?.albumName) : '';
  const [activeView, setActiveView] = useState<'popular' | 'latest' | 'moods'>('latest');
  const [latestEntries, setLatestEntries] = useState<LatestTrackEntry[]>([]);
  const [isLatestLoading, setIsLatestLoading] = useState(false);
  const latestReleaseLimit = INITIAL_LATEST_RELEASES;

  const sortedAlbums = useMemo(() => {
    return (albums || [])
      .filter((album) => album.album_group !== 'appears_on')
      .sort((a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime());
  }, [albums]);

  useEffect(() => {
    if (!artistName || sortedAlbums.length === 0) return;

    let cancelled = false;

    const loadLatestTracks = async () => {
      setIsLatestLoading(true);
      const entries: LatestTrackEntry[] = [];
      const requiredReleaseCount = mobileLatestOnly ? MOBILE_VISIBLE_RELEASES : latestReleaseLimit + 1;

      for (const album of sortedAlbums) {
        const currentReleaseCount = filterFeaturedRelease(
          buildLatestGroups(entries, artistName),
          featuredReleaseId,
          featuredReleaseName
        ).length;

        if (currentReleaseCount >= requiredReleaseCount) break;

        try {
          const albumDetail = await fetchAlbumDetail(artistName, album);
          const tracks: SpotifyTrackItem[] = albumDetail?.tracks?.items || [];

          tracks.forEach((track) => {
            if (shouldIncludeSpotifyTrackForArtist(album, track, artistName)) {
              entries.push({
                album: {
                  ...album,
                  total_tracks: albumDetail?.total_tracks || album.total_tracks,
                  images: albumDetail?.images || album.images,
                  album_type: albumDetail?.album_type || album.album_type,
                  release_date: albumDetail?.release_date || album.release_date,
                },
                track,
              });
            }
          });
        } catch (error) {
          console.error('Error loading latest tracks:', error);
        }
      }

      if (!cancelled) {
        setLatestEntries(entries);
        setIsLatestLoading(false);
      }
    };

    loadLatestTracks();

    return () => {
      cancelled = true;
    };
  }, [artistName, sortedAlbums, latestReleaseLimit, mobileLatestOnly, featuredReleaseId, featuredReleaseName]);

  const tracksWithPlayData = useMemo(() => {
    return (topTracks || [])
      .slice(0, 5)
      .map((track) => {
        const playData = popularTracks.find((p) => p.id === track.id);
        return {
          ...track,
          playCount: playData?.playCount || 0,
        };
      })
      .sort((a, b) => b.playCount - a.playCount);
  }, [popularTracks, topTracks]);

  const latestGroups = useMemo(() => {
    const groups = filterFeaturedRelease(buildLatestGroups(latestEntries, artistName), featuredReleaseId, featuredReleaseName);

    return mobileLatestOnly ? groups.slice(0, MOBILE_VISIBLE_RELEASES) : groups.slice(0, latestReleaseLimit);
  }, [artistName, featuredReleaseId, featuredReleaseName, latestEntries, latestReleaseLimit, mobileLatestOnly]);

  const isCompanionArtist = safeStringWithHyphens(artistName || '') === COMPANION_ARTIST_SLUG;
  const moodPlaylists = useQuery(
    api.companionPlaylists.listMoodPlaylists,
    isCompanionArtist && !mobileLatestOnly ? {} : 'skip'
  );
  const showMoodTab = Boolean(moodPlaylists && moodPlaylists.length > 0);
  const activeTabValue = activeView === 'moods' && !showMoodTab ? 'latest' : activeView;
  const showMoods = !mobileLatestOnly && activeTabValue === 'moods';
  const showLatest = mobileLatestOnly || activeTabValue === 'latest';
  const showPopular = !mobileLatestOnly && activeTabValue === 'popular';

  const latestAppleMusicQueries = useMemo(() => {
    return latestGroups.map((release) => ({
      artistName,
      releaseName: release.title,
      albumName: release.title,
      lookupType: (release.type === 'single' ? 'song' : 'album') as 'song' | 'album',
      spotifyId: release.id,
    }));
  }, [artistName, latestGroups]);

  const popularAppleMusicQueries = useMemo(() => {
    return tracksWithPlayData.map((track) => ({
      artistName,
      releaseName: track.name,
      albumName: track.album?.name,
      lookupType: 'song' as const,
      spotifyId: track.id,
    }));
  }, [artistName, tracksWithPlayData]);

  const { enrichmentByKey: latestAppleMusicByKey } = useAppleMusicEnrichment(latestAppleMusicQueries, {
    enabled: showLatest && !isLatestLoading,
    ttlMs: API_CACHE_TTL.SIX_HOURS,
    concurrency: 3,
  });

  const { enrichmentByKey: popularAppleMusicByKey } = useAppleMusicEnrichment(popularAppleMusicQueries, {
    enabled: showPopular,
    ttlMs: API_CACHE_TTL.SIX_HOURS,
    concurrency: 2,
  });

  const latestGroupsWithAppleMusic = useMemo(() => {
    return latestGroups.map((release) => {
      const query = {
        artistName,
        releaseName: release.title,
        albumName: release.title,
        lookupType: (release.type === 'single' ? 'song' : 'album') as 'song' | 'album',
        spotifyId: release.id,
      };
      const enrichment = latestAppleMusicByKey[getAppleMusicEnrichmentKey(query)];
      return {
        ...release,
        appleMusicUrl: enrichment?.url,
        appleMusicArtworkUrl: enrichment?.artworkUrl,
      };
    });
  }, [artistName, latestAppleMusicByKey, latestGroups]);

  if (showPopular && (!topTracks || topTracks.length === 0)) return null;
  if (showLatest && latestGroupsWithAppleMusic.length === 0 && !isLatestLoading) return null;

  return (
    <Box
      className={showMoods ? 'MoodPlaylists' : showLatest ? 'LatestTracks' : 'PopularTracks'}
      sx={{
        width: '100%',
        mt: { xs: mobileLatestOnly ? 0 : '4rem', md: '3rem' },
      }}
    >
      {!mobileLatestOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: '2.5rem', md: '1.5rem' },
            borderBottom: '1px solid var(--color1)',
          }}
        >
          <Tabs
            value={activeTabValue}
            onChange={(_, value) => setActiveView(value)}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              '.MuiTabs-indicator': { backgroundColor: 'var(--logoColor2)', height: '3px' },
              '.MuiButtonBase-root': {
                color: 'var(--logoColor)',
                fontFamily: 'Hanson',
                fontWeight: 700,
                fontSize: { xs: '3rem', sm: '1.5rem' },
                textTransform: 'capitalize',
                padding: { xs: '3rem 3rem 5rem', md: '.75rem 1.75rem 2.625rem' },
                borderTopLeftRadius: '1rem',
                borderTopRightRadius: '1rem',
                opacity: 0.58,
                '&.Mui-selected': { color: 'var(--logoColor)', opacity: 1 },
              },
            }}
          >
            <Tab label="Latest" value="latest" />
            <Tab label="Popular" value="popular" />
            {showMoodTab ? <Tab label="Mood playlists" value="moods" sx={{ whiteSpace: "nowrap" }} /> : null}
          </Tabs>
          {showPopular && totalPlays > 0 && (
            <Typography
              sx={{
                color: 'var(--logoColor2)',
                fontSize: { xs: '2rem', md: '1.1rem' },
                fontFamily: 'Poppins',
                opacity: 0.8,
              }}
            >
              {totalPlays.toLocaleString()} plays
            </Typography>
          )}
        </Box>
      )}

      {showPopular && (
        <Box sx={{ ...twoColumnListSx(tracksWithPlayData.length), gap: { xs: '1rem', md: '0.5rem 3rem' } }}>
          {tracksWithPlayData.map((track, index) => (
            <motion.div
              key={track.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
            >
              <Box
                component={RouterLink}
                to={getArtistPath(artistName, `/songs/${safeStringWithHyphens(track.name)}`)}
                onClick={() =>
                  trackClick('popular-track', {
                    label: track.name,
                    section: 'popular-tracks',
                    artist: artistName,
                    track: track.name,
                    trackId: track.id,
                    href: getArtistPath(artistName, `/songs/${safeStringWithHyphens(track.name)}`),
                  })
                }
                viewTransition
                data-apple-music-url={
                  popularAppleMusicByKey[getAppleMusicEnrichmentKey({
                    artistName,
                    releaseName: track.name,
                    albumName: track.album?.name,
                    lookupType: 'song',
                    spotifyId: track.id,
                  })]?.url || undefined
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: '1.5rem', md: '1rem' },
                  padding: { xs: '1rem 0.5rem', md: '0.5rem' },
                  borderRadius: '0.5rem',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  backgroundColor: index === 0 && track.playCount > 0 ? 'rgba(var(--logoColor2-rgb), 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--logoColor)',
                    fontSize: { xs: '2rem', md: '1.25rem' },
                    fontWeight: 600,
                    width: { xs: '2.5rem', md: '2rem' },
                    opacity: 0.5,
                  }}
                >
                  {index + 1}
                </Typography>

                {track.album?.images?.[0]?.url && (
                  <Box
                    component="img"
                    {...sharedProps('art', track.id)}
                    src={getOptimizedImageUrl(track.album.images[0].url, { width: 80, quality: 80 })}
                    alt={track.name}
                    sx={{
                      width: { xs: '5rem', md: '3rem' },
                      height: { xs: '5rem', md: '3rem' },
                      borderRadius: '0.25rem',
                      objectFit: 'cover',
                    }}
                  />
                )}

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    {...sharedProps('title', track.id)}
                    sx={{
                      color: 'var(--logoColor)',
                      fontSize: { xs: '2.5rem', md: '1.5rem' },
                      fontWeight: 500,
                      width: 'fit-content',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {track.name}
                  </Typography>
                  {track.album?.name && (
                    <Typography
                      sx={{
                        color: 'var(--logoColor)',
                        fontSize: { xs: '1.75rem', md: '1.125rem' },
                        opacity: 0.6,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {track.album.name}
                    </Typography>
                  )}
                </Box>

                {track.playCount > 0 && <PlayCountBadge count={track.playCount} />}
              </Box>
            </motion.div>
          ))}
        </Box>
      )}

      {showMoods && (
        <Box sx={{ ...twoColumnListSx((moodPlaylists || []).length), gap: { xs: '1rem', md: '0.75rem 3rem' } }}>
          {(moodPlaylists || []).length === 0 && (
            <Typography
              sx={{
                color: 'var(--logoColor)',
                fontFamily: 'Poppins',
                fontSize: { xs: '1.75rem', md: '1.125rem' },
                opacity: 0.72,
              }}
            >
              No mood playlists are ready yet. They appear here once the catalog has been analysed.
            </Typography>
          )}

          {(moodPlaylists || []).map((mood, index) => (
            <motion.div
              key={mood.moodId}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.04, duration: 0.25 }}
            >
              <Box
                component="a"
                href={mood.spotifyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackClick(`mood-playlist:${mood.moodId}`, {
                    label: mood.name,
                    section: 'popular-tracks',
                    artist: artistName,
                    href: mood.spotifyUrl,
                  })
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: '1.25rem', md: '1rem' },
                  width: '100%',
                  minHeight: { xs: '9.4rem', md: 'auto' },
                  p: { xs: '1rem 1.15rem', md: '0.5rem' },
                  borderRadius: { xs: '8px', md: '0.5rem' },
                  border: 0,
                  backgroundColor: { xs: 'var(--logoColor)', md: 'transparent' },
                  color: 'var(--logoColor)',
                  textDecoration: 'none',
                  transition: 'transform 0.2s ease, background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    transform: { xs: 'translateY(-2px)', md: 'translateX(4px)' },
                  },
                }}
              >
                <Typography
                  sx={{
                    color: { xs: 'var(--color5)', md: 'var(--logoColor)' },
                    fontSize: { xs: '2rem', md: '1.25rem' },
                    fontWeight: 600,
                    width: { xs: '2.5rem', md: '2rem' },
                    opacity: 0.5,
                  }}
                >
                  {index + 1}
                </Typography>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      color: { xs: 'var(--color5)', md: 'var(--logoColor)' },
                      fontFamily: 'Poppins',
                      fontWeight: { xs: 700, md: 500 },
                      fontSize: { xs: '2rem', md: '1.5rem' },
                      lineHeight: 1.15,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mood.name}
                  </Typography>
                  <Typography
                    sx={{
                      color: { xs: 'var(--color5)', md: 'var(--logoColor)' },
                      fontFamily: 'Poppins',
                      fontSize: { xs: '1.35rem', md: '1rem' },
                      lineHeight: 1.25,
                      mt: { xs: '0.4rem', md: '0.25rem' },
                      opacity: { xs: 0.86, md: 0.6 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {mood.description}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: '0.65rem', md: '0.5rem' },
                      mt: { xs: '0.55rem', md: '0.35rem' },
                      minHeight: { xs: '1.8rem', md: '1.25rem' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: { xs: '1.8rem', md: '1rem' },
                        px: { xs: '0.75rem', md: '0.45rem' },
                        py: { xs: '0.24rem', md: '0.1rem' },
                        borderRadius: '999px',
                        backgroundColor: { xs: 'var(--color5)', md: 'var(--logoColor2)' },
                        color: { xs: 'var(--logoColor)', md: 'var(--color5)' },
                        fontFamily: 'Poppins',
                        fontWeight: 800,
                        fontSize: { xs: '1.12rem', md: '0.62rem' },
                        lineHeight: 1,
                        textTransform: 'uppercase',
                        letterSpacing: 0,
                        flexShrink: 0,
                      }}
                    >
                      {mood.trackCount} {mood.trackCount === 1 ? 'song' : 'songs'}
                    </Box>
                    <Typography
                      sx={{
                        color: { xs: 'var(--color5)', md: 'var(--logoColor2)' },
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: { xs: '1.18rem', md: '1.125rem' },
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        opacity: { xs: 0.86, md: 0.6 },
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: { xs: '1.8rem', md: '1rem' },
                      }}
                    >
                      Open on Spotify
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>
      )}

      {showLatest && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '1rem', md: '0.75rem' },
          }}
        >
          {isLatestLoading && latestGroups.length === 0 && <LatestReleaseSkeleton />}

          <Box sx={{ ...twoColumnListSx(mobileLatestOnly ? 0 : latestGroupsWithAppleMusic.length), gap: { xs: '1rem', md: '0.75rem 3rem' } }}>
            {latestGroupsWithAppleMusic.map((release, index) => (
              <motion.div
                key={release.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04, duration: 0.25 }}
              >
                <Box
                  component={RouterLink}
                  to={release.path}
                  onClick={() =>
                    trackClick('latest-release-card', {
                      label: release.title,
                      section: 'popular-tracks',
                      artist: artistName,
                      releaseName: release.title,
                      href: release.path,
                    })
                  }
                  viewTransition
                  data-apple-music-url={release.appleMusicUrl || undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: '1.25rem', md: '1rem' },
                    width: '100%',
                    minHeight: { xs: '9.4rem', md: 'auto' },
                    p: { xs: '1rem 1.15rem', md: '0.5rem' },
                    borderRadius: { xs: '8px', md: '0.5rem' },
                    border: 0,
                    backgroundColor: { xs: 'var(--logoColor)', md: 'transparent' },
                    color: 'var(--logoColor)',
                    textDecoration: 'none',
                    transition: 'transform 0.2s ease, background-color 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.07)',
                      transform: { xs: 'translateY(-2px)', md: 'translateX(4px)' },
                    },
                  }}
                >
                  {!mobileLatestOnly && (
                    <Typography
                      component="span"
                      sx={{
                        color: 'var(--logoColor)',
                        fontSize: { xs: '2rem', md: '1.25rem' },
                        fontWeight: 600,
                        width: { xs: '2.5rem', md: '2rem' },
                        opacity: playingReleaseKey && playingReleaseKey === normalizeReleaseKey(release.title) ? 1 : 0.5,
                        display: 'inline-flex',
                      }}
                    >
                      {playingReleaseKey && playingReleaseKey === normalizeReleaseKey(release.title) ? (
                        <NowPlayingBars playing size={16} color="var(--logoColor2)" label="Now playing" />
                      ) : (
                        index + 1
                      )}
                    </Typography>
                  )}
                  {release.image && (
                    <Box
                      component="img"
                      {...sharedProps('art', release.id)}
                      src={getOptimizedImageUrl(release.appleMusicArtworkUrl || release.image, { width: 160, height: 160, fit: 'cover', quality: 82 })}
                      alt={release.title}
                      sx={{
                        width: { xs: '8rem', md: '3rem' },
                        height: { xs: '8rem', md: '3rem' },
                        borderRadius: { xs: '6px', md: '0.25rem' },
                        objectFit: 'cover',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      {...sharedProps('title', release.id)}
                      sx={{
                        color: { xs: 'var(--color5)', md: 'var(--logoColor)' },
                        fontFamily: 'Poppins',
                        fontWeight: { xs: 700, md: 500 },
                        width: 'fit-content',
                        maxWidth: '100%',
                        fontSize: { xs: '2rem', md: '1.5rem' },
                        lineHeight: 1.15,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {release.title}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: '0.65rem', md: '0.5rem' },
                        mt: { xs: '0.55rem', md: '0.35rem' },
                        minHeight: { xs: '1.8rem', md: '1.25rem' },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: { xs: '1.8rem', md: '1rem' },
                          px: { xs: '0.75rem', md: '0.45rem' },
                          py: { xs: '0.24rem', md: '0.1rem' },
                          borderRadius: '999px',
                          backgroundColor: { xs: 'var(--color5)', md: 'var(--logoColor2)' },
                          color: { xs: 'var(--logoColor)', md: 'var(--color5)' },
                          fontFamily: 'Poppins',
                          fontWeight: 800,
                          fontSize: { xs: '1.12rem', md: '0.62rem' },
                          lineHeight: 1,
                          textTransform: 'uppercase',
                          letterSpacing: 0,
                          flexShrink: 0,
                        }}
                      >
                        {getReleaseTypeLabel(release.type)}
                      </Box>
                      <Typography
                        sx={{
                          color: { xs: 'var(--color5)', md: 'var(--logoColor2)' },
                          fontFamily: 'Poppins',
                          fontWeight: 600,
                          fontSize: { xs: '1.18rem', md: '1.125rem' },
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                          opacity: { xs: 0.86, md: 0.6 },
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: { xs: '1.8rem', md: '1rem' },
                        }}
                      >
                        {formatDate(release.releaseDate)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Box>

          {isLatestLoading && latestGroups.length > 0 && (
            <Typography sx={{ color: 'var(--logoColor)', fontFamily: 'Poppins', fontSize: { xs: '1.5rem', md: '0.875rem' }, opacity: 0.72 }}>
              Loading latest releases...
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
